import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import PhotoGrid from './photo-grid'
import Lightbox, { type LightboxItem } from './lightbox'
import LinkUnavailable from './link-unavailable'
import ViewMessage from './view-message'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import type { ViewAlbumResponse } from '../../../../types'

export default function PublicAlbumView() {
  const { token } = useParams<{ token: string }>()
  const [album, setAlbum] = useState<ViewAlbumResponse | null>(null)
  const [status, setStatus] = useState<'loading' | 'ok' | 'gone' | 'throttled' | 'failed'>('loading')
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // Bumped by the retry control to re-run the fetch effect.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    if (!token) { setStatus('gone'); return }
    setStatus('loading')
    apiClient.getViewAlbum(token)
      .then((data) => { if (!cancelled) { setAlbum(data); setStatus('ok') } })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 404) setStatus('gone')
        else if (err instanceof ApiError && err.status === 429) setStatus('throttled')
        else setStatus('failed')
      })
    return () => { cancelled = true }
  }, [token, attempt])

  if (status === 'loading') return <ViewMessage title="Gathering these memories…" />
  // One message for unknown, revoked, mismatched and expired alike: the API
  // returns an identical opaque 404 for all of them, and the UI must not
  // reintroduce the oracle the backend deliberately removed.
  if (status === 'gone') return <LinkUnavailable />
  // Not the visitor's fault — they may simply be behind a shared network — so
  // this states the limit without accusing them of anything.
  if (status === 'throttled')
    return (
      <ViewMessage
        title="This album needs a moment"
        note="Give it a little while, then try opening the link again."
      />
    )
  // Unlike 'gone' and 'throttled', this one is worth retrying in place — a 500
  // or a dropped connection says nothing about the link itself.
  if (status === 'failed' || !album) {
    return (
      <ViewMessage
        title="These memories didn’t come through"
        action={
          <button className="primary-button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        }
      />
    )
  }

  const gridItems = album.files.map((f) => ({
    id: f.id,
    src: apiClient.toAbsolute(f.thumbUrl),
    label: f.label,
  }))
  const lightboxItems: LightboxItem[] = album.files.map((f) => ({
    id: f.id,
    src: apiClient.toAbsolute(f.mediumUrl),
    label: f.label,
    downloadHref: apiClient.toAbsolute(f.downloadUrl),
  }))

  return (
    <div className={`public-view${album.files.length === 0 ? ' public-view--empty' : ''}`}>
      <h1 className="public-view__title">{album.name}</h1>
      {/* No count on an empty album — "0 photos" above "waiting to be told"
          says the same thing twice, the second time badly. */}
      {album.files.length > 0 && (
        <p className="public-view__count">
          {album.files.length} {album.files.length === 1 ? 'photo' : 'photos'}
        </p>
      )}
      {album.files.length === 0 ? (
        <p className="public-view__empty">This story is waiting to be told</p>
      ) : (
        <PhotoGrid items={gridItems} onItemClick={(_item, index) => setOpenIndex(index)} />
      )}
      {openIndex !== null && (
        <Lightbox
          items={lightboxItems}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </div>
  )
}
